import React, { createContext, useContext } from "react";
import { MenuIcon } from "../ui/Icons";

/** What the shell hands a screen so it can open the account menu over itself. */
interface AccountMenu {
  /** Opens the menu. See `AppLayout`, which owns what that means. */
  open: () => void;
  /** Whether it is already open, for the button that opened it. */
  isOpen: boolean;
}

/**
 * The way into the account menu, passed down rather than drawn by the shell.
 *
 * The shell used to carry a bar of its own above every screen with this button
 * in it, which cost a phone a whole row to say one thing. The dashboard already
 * heads itself with the reader's name, so the button belongs at the far end of
 * that row and the bar is gone. What is left for the shell to own is the
 * opening itself, which is a step in the history rather than a flag anybody
 * can set — and the shell still hands it to every screen under it, because the
 * menu is the shell's and it is only ever one screen that asks.
 */
export const AccountMenuContext = createContext<AccountMenu | null>(null);

/**
 * The three-line button, at the right of the row that heads the home screen.
 *
 * Home only. It was on every screen's title row once, which put a way into
 * the whole app beside the arrow out of the one record the reader had open —
 * two ways out of the same screen, and the menu the louder of them. A phone
 * leaves a screen by going back and reaches the app from home, so the button
 * is drawn by `MobileWelcome` and nowhere else.
 *
 * A phone only there too: from `md` up the rail is open and the account sits
 * at the foot of it. Nothing at all outside the shell — the trial screens and
 * the unit tests that mount a greeting on its own have no menu to open, and an
 * inert button there would be a promise the screen cannot keep.
 */
export const MobileMenuButton: React.FC<{ className?: string }> = ({
  className = "",
}) => {
  const menu = useContext(AccountMenuContext);
  if (!menu) return null;

  return (
    <button
      type="button"
      onClick={menu.open}
      aria-label="Open account menu"
      aria-expanded={menu.isOpen}
      // The negative margins are the same trick the back arrow uses at the
      // other end of the row: 40px of touch target, sat on the heading's line
      // rather than above it, and flush with the page's own gutter.
      className={`md:hidden -mr-2.5 -my-2 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-ink transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2CB]/40 ${className}`}
    >
      <MenuIcon className="h-6 w-6" />
    </button>
  );
};

export default MobileMenuButton;
